import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import AppAlert from "../../components/AppAlert";
import AppButton from "../../components/AppButton";
import { VARIABLE_LABELS } from "./constants";
import { fetchAvailableFeatures, trainCustomModel } from "./services";

const ACTIVE_COLOR = "primary.main";

const CustomModelTrainer = ({
  trainingStatus,
  modelFeatures,
  onStarted,
  onReset,
}: {
  trainingStatus?: string;
  modelFeatures?: string[];
  onStarted: () => void;
  onReset: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [selectedQuanti, setSelectedQuanti] = useState<string[]>([]);
  const [selectedQuali, setSelectedQuali] = useState<string[]>([]);

  const { data: available } = useQuery(
    "ml-available-features",
    fetchAvailableFeatures,
    {
      refetchOnWindowFocus: false,
    }
  );

  const featuresKey = modelFeatures ? [...modelFeatures].sort().join(",") : "";
  useEffect(() => {
    if (!available || featuresKey === "") return;
    const stored = featuresKey.split(",");
    setSelectedQuanti(available.quanti.filter((f) => stored.includes(f)));
    setSelectedQuali(
      available.quali.filter((f) =>
        stored.some((col) => col === f || col.startsWith(`${f}_`))
      )
    );
  }, [available, featuresKey]);

  const trainMutation = useMutation(
    () =>
      trainCustomModel({
        quanti_features: selectedQuanti,
        quali_features: selectedQuali,
      }),
    { onSuccess: onStarted }
  );

  const toggle = (
    list: string[],
    setList: (v: string[]) => void,
    value: string
  ) => {
    setList(
      list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
    );
  };

  const isTraining =
    trainingStatus === "pending" || trainingStatus === "running";
  const noneSelected =
    selectedQuanti.length === 0 && selectedQuali.length === 0;

  const chipSx = (selected: boolean): SxProps<Theme> => ({
    fontSize: "0.75rem",
    bgcolor: selected ? ACTIVE_COLOR : "transparent",
    color: selected ? "primary.contrastText" : "text.secondary",
    border: "1px solid",
    borderColor: selected ? ACTIVE_COLOR : "divider",
    opacity: isTraining ? 0.5 : 1,
    pointerEvents: isTraining ? "none" : "auto",
    "&:hover": { bgcolor: selected ? "primary.dark" : "action.hover" },
  });

  return (
    <Paper elevation={1} sx={{ overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
          userSelect: "none",
          px: 2,
          py: 1.5,
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <Typography
          sx={{
            fontWeight: 600,
            flexGrow: 1,
          }}
        >
          Créer un modèle personnalisé
        </Typography>
        {isTraining && (
          <CircularProgress size={18} sx={{ color: ACTIVE_COLOR }} />
        )}
        {trainingStatus === "done" && (
          <Chip
            size="small"
            label="Entraînement terminé ✓"
            sx={{
              bgcolor: ACTIVE_COLOR,
              color: "primary.contrastText",
              fontWeight: 600,
            }}
          />
        )}
        {isTraining && (
          <AppButton
            intent="tonal"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onReset();
            }}
            sx={{ fontSize: "0.7rem", whiteSpace: "nowrap" }}
          >
            Réinitialiser
          </AppButton>
        )}
        {/* The row stays clickable for the mouse, but the toggle itself is this button: the
            row cannot be one, it already contains the reset button. */}
        <IconButton
          size="small"
          aria-expanded={open}
          aria-label="Créer un modèle personnalisé"
          onClick={(event) => {
            event.stopPropagation();
            setOpen((v) => !v);
          }}
        >
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={open}>
        <Divider />
        <Box
          sx={{
            px: 2,
            py: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
            }}
          >
            Sélectionnez vos variables et entraînez un modèle à comparer avec le
            modèle standard.
          </Typography>

          {isTraining && (
            <AppAlert severity="info" sx={{ py: 0.5 }}>
              Entraînement en cours — environ 1 à 2 minutes. La page se met à
              jour automatiquement.
            </AppAlert>
          )}
          {trainingStatus === "error" && (
            <AppAlert severity="error" sx={{ py: 0.5 }}>
              L'entraînement a échoué. Vérifiez les logs Celery.
            </AppAlert>
          )}

          {available && (
            <>
              <Box>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                    }}
                  >
                    Numériques
                  </Typography>
                  {selectedQuanti.length > 0 && (
                    <Chip
                      size="small"
                      label={selectedQuanti.length}
                      sx={{
                        height: 18,
                        fontSize: "0.7rem",
                        bgcolor: ACTIVE_COLOR,
                        color: "primary.contrastText",
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {available.quanti.map((f) => (
                    <Chip
                      key={f}
                      size="small"
                      label={VARIABLE_LABELS[f] ?? f}
                      onClick={() =>
                        toggle(selectedQuanti, setSelectedQuanti, f)
                      }
                      sx={chipSx(selectedQuanti.includes(f))}
                    />
                  ))}
                </Box>
              </Box>

              <Box>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                    }}
                  >
                    Catégorielles
                  </Typography>
                  {selectedQuali.length > 0 && (
                    <Chip
                      size="small"
                      label={selectedQuali.length}
                      sx={{
                        height: 18,
                        fontSize: "0.7rem",
                        bgcolor: ACTIVE_COLOR,
                        color: "primary.contrastText",
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {available.quali.map((f) => (
                    <Chip
                      key={f}
                      size="small"
                      label={VARIABLE_LABELS[f] ?? f}
                      onClick={() => toggle(selectedQuali, setSelectedQuali, f)}
                      sx={chipSx(selectedQuali.includes(f))}
                    />
                  ))}
                </Box>
              </Box>
            </>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 0.5 }}>
            <AppButton
              disabled={noneSelected || isTraining || trainMutation.isLoading}
              onClick={() => trainMutation.mutate()}
              startIcon={
                trainMutation.isLoading ? (
                  <CircularProgress size={14} />
                ) : undefined
              }
            >
              {isTraining ? "Entraînement en cours…" : "Entraîner le modèle"}
            </AppButton>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default CustomModelTrainer;
